import { ColumnsType } from 'antd/es/table';
import { Edit2 } from 'iconsax-react';
import SectionTitle from '../../../components/atoms/SectionTitle';
import SectionDesc from '../../../components/atoms/SectionDesc';
import CustomTable from '../../../components/atoms/CustomTable';
import { useTranslation } from 'react-i18next';
import { MarketplacePlanItem } from '../../../types/marketplacePlans';
import InfoText from '../../../components/atoms/InfoText';

interface PlansTableSectionProps {
  plans: MarketplacePlanItem[];
  loading?: boolean;
  onEdit: (record: MarketplacePlanItem) => void;
}

export default function PlansTableSection({
  plans,
  loading = false,
  onEdit,
}: PlansTableSectionProps) {
  const { t } = useTranslation();

  const columns: ColumnsType<MarketplacePlanItem> = [
    {
      title: t('home.plan-name-en'),
      dataIndex: 'planNameEn',
      key: 'planNameEn',
      render: (val: string, record) => (
        <div
          className="flex items-center gap-2"
          data-testid={`plan-name-en-container-${record.id}`}
        >
          <InfoText
            text={val}
            className="!text-light-text-primary dark:!text-dark-text-primary text-sm !font-normal"
          />
          {record.recommended && (
            <div
              className="inline-flex items-center justify-center w-5 h-5 rounded-[6px] bg-light-alert text-white"
              data-testid={`recommended-badge-en-${record.id}`}
            >
              <InfoText
                text="R"
                className="!text-white text-[11px] !font-bold leading-none"
              />
            </div>
          )}
        </div>
      ),
    },
    {
      title: t('home.plan-name-ar'),
      dataIndex: 'planNameAr',
      key: 'planNameAr',
      render: (val: string, record) => (
        <div
          className="flex items-center gap-2"
          data-testid={`plan-name-ar-container-${record.id}`}
        >
          <InfoText
            text={val}
            className="!text-light-text-primary dark:!text-dark-text-primary text-sm !font-normal"
          />
          {record.recommended && (
            <div
              className="inline-flex items-center justify-center w-5 h-5 rounded-[6px] bg-light-alert text-white"
              data-testid={`recommended-badge-ar-${record.id}`}
            >
              <InfoText
                text="R"
                className="!text-white text-[11px] !font-bold leading-none"
              />
            </div>
          )}
        </div>
      ),
    },
    {
      title: t('home.extras-monthly'),
      key: 'extrasMonthly',
      align: 'center',
      render: (_, record) => {
        const hasExtras =
          record.allowExtraProducts &&
          record.extraFeeMonthlyCoins !== null &&
          record.extraFeeMonthlyPrice !== null;

        const text = hasExtras
          ? t('home.extras-format', {
              coins: record.extraFeeMonthlyCoins,
              sar: record.extraFeeMonthlyPrice,
            })
          : '-';

        return (
          <div data-testid={`extras-monthly-${record.id}`}>
            <InfoText
              text={text}
              className="!text-light-text-primary dark:!text-dark-text-primary text-sm !font-normal"
            />
          </div>
        );
      },
    },
    {
      title: t('home.extras-yearly'),
      key: 'extrasYearly',
      align: 'center',
      render: (_, record) => {
        const hasExtras =
          record.allowExtraProducts &&
          record.extraFeeYearlyCoins !== null &&
          record.extraFeeYearlyPrice !== null;

        const text = hasExtras
          ? t('home.extras-format', {
              coins: record.extraFeeYearlyCoins,
              sar: record.extraFeeYearlyPrice,
            })
          : '-';

        return (
          <div data-testid={`extras-yearly-${record.id}`}>
            <InfoText
              text={text}
              className="!text-light-text-primary dark:!text-dark-text-primary text-sm !font-normal"
            />
          </div>
        );
      },
    },
    {
      title: t('home.yearly'),
      key: 'yearlyPrice',
      render: (_, record) => (
        <div data-testid={`yearly-price-${record.id}`}>
          <InfoText
            text={t('home.price-sar-coins-format', {
              sar: record.priceYearly,
              coins: record.coinsYearly,
            })}
            className="!text-light-text-primary dark:!text-dark-text-primary text-sm !font-normal"
          />
        </div>
      ),
    },
    {
      title: t('home.monthly'),
      key: 'monthlyPrice',
      render: (_, record) => (
        <div data-testid={`monthly-price-${record.id}`}>
          <InfoText
            text={t('home.price-sar-coins-format', {
              sar: record.priceMonthly,
              coins: record.coinsMonthly,
            })}
            className="!text-light-text-primary dark:!text-dark-text-primary text-sm !font-normal"
          />
        </div>
      ),
    },
    {
      title: t('home.limit'),
      dataIndex: 'productLimit',
      key: 'productLimit',
      render: (val: number, record) => (
        <div data-testid={`product-limit-${record.id}`}>
          <InfoText
            text={String(val)}
            className="!text-light-text-primary dark:!text-dark-text-primary text-sm !font-normal"
          />
        </div>
      ),
    },
    {
      title: t('home.action'),
      key: 'action',
      render: (_, record) => (
        <div
          className="w-8 h-8 rounded-full bg-light-information/10 dark:bg-dark-information/20 flex items-center justify-center cursor-pointer transition-colors hover:bg-light-information/20 dark:hover:bg-dark-information/30 text-light-information dark:text-dark-information"
          onClick={() => onEdit(record)}
          data-testid={`plan-edit-btn-${record.id}`}
        >
          <Edit2 size={16} className="text-light-information dark:text-dark-information" variant="Bold" />
        </div>
      ),
    },
  ];

  return (
    <div
      className="bg-light-surface-primary dark:bg-dark-surface-primary rounded-xl p-6 flex flex-col gap-6 shadow-sm border border-light-input-border dark:border-dark-input-border"
      data-testid="plans-table-section-container"
    >
      <div
        className="flex flex-col gap-1"
        data-testid="plans-table-section-header"
      >
        <SectionTitle text={t('home.plans-table-title')} />
        <SectionDesc text={t('home.plans-table-desc')} />
      </div>

      <CustomTable<MarketplacePlanItem>
        className="[&_.ant-table-thead_.ant-table-cell]:!bg-light-surface-primary dark:[&_.ant-table-thead_.ant-table-cell]:!bg-dark-surface-primary [&_.ant-table-thead_.ant-table-cell]:!border-b [&_.ant-table-thead_.ant-table-cell]:!border-light-input-border dark:[&_.ant-table-thead_.ant-table-cell]:!border-dark-input-border [&_.ant-table-thead_.ant-table-cell]:!text-light-text-primary dark:[&_.ant-table-thead_.ant-table-cell]:!text-dark-text-primary [&_.ant-table-thead_.ant-table-cell]:!font-semibold [&_.ant-table-tbody>tr>td]:!bg-light-surface-primary dark:[&_.ant-table-tbody>tr>td]:!bg-dark-surface-primary [&_.ant-table-tbody>tr>td]:!border-b [&_.ant-table-tbody>tr>td]:!border-light-input-border dark:[&_.ant-table-tbody>tr>td]:!border-dark-input-border [&_.ant-table-tbody>tr:hover>td]:!bg-soft-background dark:[&_.ant-table-tbody>tr:hover>td]:!bg-dark-surface-primary"
        columns={columns}
        dataSource={plans}
        rowKey="id"
        loading={loading}
        currentPage={0}
        totalElements={plans.length}
        onPageChange={() => {}}
        onRowClick={() => {}}
        pagination={false}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
}
